import React, { FC, ReactElement, useEffect } from 'react';
import mustache from 'mustache';
import sanitizeHtml, { Transformer } from 'sanitize-html';

import { useSettings } from '../../../hooks/useSettings';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import filterCSSStyles from './lib/filterCSSStyles';
import SkipToContent, { SkipToContentProps } from './SkipToContent';

declare global {
  interface Element {
    /**
     * IE-only prefixed version of `Element.matches()`.
     */
    msMatchesSelector: Element['matches'];
  }
}

export enum TagName {
  A = 'A',
  IMG = 'IMG',
}

const svgDescendantSelector = 'svg *';

export interface DynamicHeaderProps {
  featureName?: string;
  templateRenderingContext?: unknown;
  skipToContent?: SkipToContentProps;
  sticky?: boolean;
}

const DynamicHeader: FC<DynamicHeaderProps> = ({
  featureName,
  templateRenderingContext,
  skipToContent,
  sticky: stickyProp,
}): ReactElement | null => {
  const { trackHeader } = useSettings();

  const { html, querySelectors, sticky: stickyIn } = trackHeader || {};
  const sticky =
    stickyProp ?? (stickyIn === null || stickyIn === undefined || stickyIn);
  const extractedStyles: string[] = [];

  // TODO: Sanitize this html only on the back end.
  const htmlString = sanitizeHtml(html || '', {
    // setting these to false allows all tags and attributes
    // TODO: decide which set of tags and attributes to allow in headers
    // Can use TagType and AttributeType enums
    allowedTags: false,
    allowVulnerableTags: true,
    allowedAttributes: false,
    exclusiveFilter: (frame) => {
      // extract any style tag and remove it from the original html
      if (frame.tag === 'style') {
        extractedStyles.push(filterCSSStyles(frame.text));
        return true;
      }
      return false;
    },
    // We wrap the header html string in a <header> below, which creates nested <header>
    // elements if the header html also has a <header>. Since we wrap the html in a
    // <header>, transform any <header> elements to divs.
    transformTags: {
      header: 'div',
      h1: function (tagName, attribs) {
        // Turn off the semantic meaning of any h1 in the header HTML.
        // An h1 should be provided by the specific product instead.
        return {
          tagName,
          attribs: {
            ...attribs,
            role: 'presentation',
          },
        };
      },
    },
  });

  // for now we are allowing template variables anywhere in the header html
  const templatedHtmlString = mustache.render(
    htmlString,
    templateRenderingContext,
  );

  // Our original header html contains ids. Duplicating this header for the
  // sticky header effect creates duplicate ids, which is invalid html.
  // This destructures `id` out of attribs
  const hiddenHtmlTagTransform: Transformer = (
    tagName,
    { id, ...attribs },
  ) => ({ tagName, attribs });
  const hiddenHtmlString = sanitizeHtml(templatedHtmlString || '', {
    allowedTags: false,
    allowVulnerableTags: true,
    allowedAttributes: false,
    transformTags: {
      '*': hiddenHtmlTagTransform,
    },
  });

  const logEvent = useAnalyticsPost();
  const baseLogInfo = {
    eventComponent: NarvarEventComponents.NTH_BUNDLE_DYNAMIC_HEADER,
    eventFeature: NarvarEventComponents.NTH_BUNDLE_DYNAMIC_HEADER,
    pageComponent: NarvarEventComponents.NTH_BUNDLE_DYNAMIC_HEADER,
    feature: featureName,
  };

  useEffect(
    () => {
      logEvent({
        ...baseLogInfo,
        eventType: NarvarEventTypes.COMPONENT_RENDER,
      });
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const header = (
    <header
      role="banner"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: templatedHtmlString }}
    />
  );

  const hiddenHeader = (
    <header
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: hiddenHtmlString }}
    />
  );

  return (
    <div style={{ position: 'relative' }}>
      {skipToContent && (
        <SkipToContent
          targetId={skipToContent.targetId}
          scrollToTarget={skipToContent.scrollToTarget}
        />
      )}
      {/* NOTE: this event handler uses events that have been bubbled up. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        style={{
          whiteSpace: 'normal',
          ...(sticky && {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 99,
          }),
        }}
        onClick={(event): void => {
          const element = event.target;

          if (!(element instanceof Element)) return;

          const href = element.getAttribute('href');
          const { tagName, innerHTML: clickLabel } = element;

          let isImage = false;

          switch (tagName) {
            case TagName.IMG: {
              isImage = true;
              break;
            }
            case TagName.A: {
              break;
            }
            default: {
              if (
                (!element.matches && !element.msMatchesSelector) ||
                (element.matches && !element.matches(svgDescendantSelector)) ||
                (element.msMatchesSelector &&
                  !element.msMatchesSelector(svgDescendantSelector))
              ) {
                return;
              }

              isImage = true;
            }
          }

          const logoAssetPayload: Pick<
            Parameters<typeof logEvent>[0],
            | 'clickAssetId'
            | 'clickAssetType'
            | 'clickAssetUrl'
            | 'clickCategory'
            | 'clickSubType'
          > = isImage
            ? {
                clickAssetId: (querySelectors || []).find((querySelector) => {
                  if (!querySelector) return false;
                  if (element.matches) return element.matches(querySelector);
                  if (element.msMatchesSelector)
                    return element.msMatchesSelector(querySelector);
                  return false;
                }),
                clickAssetType: tagName,
                clickAssetUrl: href,
                clickCategory: NarvarClickSubTypes.LOGO_ASSET,
                clickSubType: NarvarClickSubTypes.LOGO_ASSET,
              }
            : {};

          logEvent({
            ...baseLogInfo,
            clickCategory: NarvarClickSubTypes.NAVIGATION_ELEMENT,
            clickLinkDestinationUrl: href,
            clickLinkLabel: clickLabel,
            clickSubType: NarvarClickSubTypes.NAVIGATION_ELEMENT,
            clickType: NarvarClickTypes.OUTGOING,
            eventType: NarvarEventTypes.CLICK,
            ...logoAssetPayload,
          });
        }}
      >
        {extractedStyles.length > 0 && (
          <style>{extractedStyles.join(' ')}</style>
        )}
        {header}
      </div>
      {/*
        `position: fixed` does not leave a gap in the page where it would
        normally have been located. It's similar to `position: absolute`. The
        header is duplicated with visibility set to hidden for the second, to
        recreate that gap without knowing its height.
      */}
      {sticky && (
        <div aria-hidden style={{ visibility: 'hidden', whiteSpace: 'normal' }}>
          {hiddenHeader}
        </div>
      )}
    </div>
  );
};

export default DynamicHeader;
